import {
  IClinic,
  IPortRequestAddressSnapshot,
  IPortRequestClinicSnapshot,
  ITwilioPortRequest,
} from '@dataTypes/clinic.type'

export type PortRequestDeclineInfo = {
  reason: string | null
  note: string | null
}

export const isFaxPortRequest = (request: ITwilioPortRequest): boolean =>
  request.channel === 'fax' || request.provider === 'sinch'

export const getPortRequestTypeLabel = (
  request: ITwilioPortRequest,
): 'Fax' | 'Phone' => (isFaxPortRequest(request) ? 'Fax' : 'Phone')

export const getPortRequestSourceLabel = (
  request: ITwilioPortRequest,
): string => {
  if (request.source_module === 'fax_settings') return 'Fax Settings'
  if (request.source_module === 'phone_management') return 'Phone Management'
  return 'Unknown source'
}

export const getPortRequestDeclineInfo = (
  request: ITwilioPortRequest,
): PortRequestDeclineInfo => {
  const reason = request.rejection_reason?.trim() || null
  const note = request.general_note?.trim() || null
  if (reason || note) {
    return { reason, note }
  }

  const declinedEvent = [...(request.review_history ?? [])]
    .reverse()
    .find((entry) => entry.event?.toLowerCase() === 'declined')

  return {
    reason: declinedEvent?.reason?.trim() || null,
    note: declinedEvent?.note?.trim() || null,
  }
}

type ClinicAddressSource = Pick<
  IClinic,
  | 'clinic_name'
  | 'address'
  | 'address_line_2'
  | 'city'
  | 'state'
  | 'zip'
  | 'country_code'
>

export const toPortRequestClinicSnapshot = (
  clinic: ClinicAddressSource | null | undefined,
): IPortRequestClinicSnapshot | null => {
  if (!clinic?.clinic_name?.trim()) return null

  return {
    clinic_name: clinic.clinic_name.trim(),
    address: {
      street: clinic.address?.trim() || '',
      street_2: clinic.address_line_2?.trim() || null,
      city: clinic.city?.trim() || '',
      state: clinic.state?.trim() || '',
      zip: clinic.zip?.trim() || '',
      country: clinic.country_code?.trim() || 'US',
    },
  }
}

const toTwilioAddress = (
  address: IPortRequestAddressSnapshot,
): IPortRequestAddressSnapshot => ({
  street: address.street,
  street_2: address.street_2 ?? null,
  city: address.city,
  state: address.state,
  zip: address.zip,
  country: address.country,
})

export const buildTwilioPortProviderPayload = (params: {
  clinic: IPortRequestClinicSnapshot
  phone_numbers: string[]
  phone_carrier: string
  representative_name: string
  representative_email: string
  wireless_phone_number: string | null
  wireless_account_number: string | null
  wireless_pin_or_ssn: string | null
  twilio_port_document_id: string | null
  document_sids?: string[]
}): Record<string, unknown> => {
  const losingCarrierInformation: Record<string, unknown> = {
    customer_type: 'Business',
    customer_name: params.clinic.clinic_name,
    authorized_representative: params.representative_name,
    authorized_representative_email: params.representative_email,
    losing_carrier_name: params.phone_carrier,
    address: toTwilioAddress(params.clinic.address),
  }

  if (params.wireless_account_number) {
    losingCarrierInformation.account_number = params.wireless_account_number
  }
  if (params.wireless_phone_number) {
    losingCarrierInformation.account_telephone_number =
      params.wireless_phone_number
  }
  if (params.wireless_pin_or_ssn) {
    losingCarrierInformation.pin_or_ssn = params.wireless_pin_or_ssn
  }

  return {
    customer_type: 'Business',
    company_customer_name: params.clinic.clinic_name,
    phone_number: params.phone_numbers[0] || null,
    phone_numbers: params.phone_numbers,
    phone_carrier: params.phone_carrier,
    document_sids: params.document_sids ?? [],
    twilio_port_document_id: params.twilio_port_document_id,
    wireless_phone_number: params.wireless_phone_number,
    wireless_account_number: params.wireless_account_number,
    wireless_pin_or_ssn: params.wireless_pin_or_ssn,
    losing_carrier_information: losingCarrierInformation,
    clinic: params.clinic,
  }
}

export const buildSinchPortProviderPayload = (params: {
  clinic: IPortRequestClinicSnapshot
  phone_numbers: string[]
  phone_carrier: string
  representative_name: string
  representative_email: string
  wireless_account_number: string | null
  wireless_pin_or_ssn: string | null
}): Record<string, unknown> => ({
  loaAuthorizingPerson: params.representative_name,
  contactEmail: params.representative_email,
  carrier: params.phone_carrier,
  phoneNumbers: params.phone_numbers,
  clinic_name: params.clinic.clinic_name,
  address: params.clinic.address,
  ...(params.wireless_account_number
    ? { accountNumber: params.wireless_account_number }
    : {}),
  ...(params.wireless_pin_or_ssn ? { pin: params.wireless_pin_or_ssn } : {}),
  clinic: params.clinic,
})

export const getPortRequestAddressFromPayload = (
  payload: Record<string, unknown> | null | undefined,
): IPortRequestAddressSnapshot | null => {
  if (!payload) return null

  const clinic = payload.clinic as IPortRequestClinicSnapshot | undefined
  if (clinic?.address?.street) return clinic.address

  const address = payload.address as IPortRequestAddressSnapshot | undefined
  if (address?.street) return address

  const losing = payload.losing_carrier_information as
    | { address?: IPortRequestAddressSnapshot }
    | undefined
  if (losing?.address?.street) return losing.address

  return null
}
